import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import api from "@/api";
import { useI18n } from "@/i18n";

// A counter alone never says whether it is public: the admin read 286 while a visitor saw
// 151 and nobody could tell why (18/09). Tracked / public / hidden, with the reason one
// click away.
export default function PublicCountersPanel() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/watching-audit");
      setData(r.data);
    } catch {
      setData(null);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const c = data.counters;
  const hidden = c.admin_dashboard_players - c.public_watchlist;
  const other = data.other_counters || {};

  return (
    <div className="mb-6 rounded-xl p-5 hud-card" data-testid="public-counters-panel">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
        <span className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("admin.counters_title")}</span>
        <span className="font-mono2 text-xs text-slate-200" data-testid="counter-tracked">
          <b className="font-display text-2xl text-[#00F0FF]">{c.admin_dashboard_players}</b> {t("admin.counter_tracked")}
        </span>
        <span className="font-mono2 text-xs text-slate-200" data-testid="counter-public">
          <b className="font-display text-2xl text-[#00FF87]">{c.public_watchlist}</b> {t("admin.counter_public")}
        </span>
        <button
          onClick={() => setOpen(!open)}
          data-testid="counter-hidden-toggle"
          className="flex items-center gap-1 font-mono2 text-xs text-[#FFD600] underline-offset-4 hover:underline"
        >
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          <b className="font-display text-2xl">{hidden}</b> {t("admin.counter_hidden")}
        </button>
      </div>

      {open && (
        <div className="mt-4 space-y-3" data-testid="counter-hidden-breakdown">
          <p className="font-mono2 text-[10px] text-slate-500">{data.public_query?.conditions}</p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] font-mono2 text-[10px]">
              <thead className="text-slate-500">
                <tr className="text-left uppercase tracking-wider">
                  <th className="py-1">{t("admin.counter_league")}</th>
                  <th className="py-1 text-right">{t("admin.counter_tracked")}</th>
                  <th className="py-1 text-right">{t("admin.counter_public")}</th>
                  <th className="py-1 text-right">{t("admin.counter_hidden")}</th>
                  <th className="py-1 pl-4">{t("admin.counter_reasons")}</th>
                </tr>
              </thead>
              <tbody className="text-slate-300">
                {data.by_league.map((r) => (
                  <tr key={r.league} className="border-t border-slate-800/60" data-testid={`counter-row-${r.league}`}>
                    <td className="py-1">{r.league}</td>
                    <td className="py-1 text-right">{r.watching}</td>
                    <td className="py-1 text-right text-[#00FF87]">{r.public}</td>
                    <td className="py-1 text-right text-[#FFD600]">{r.hidden}</td>
                    <td className="py-1 pl-4 text-slate-400">
                      {Object.entries(r.hidden_reasons).filter(([, n]) => n > 0).map(([k, n]) => `${k}=${n}`).join(" · ") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1 border-t border-slate-800/60 pt-2 font-mono2 text-[10px] text-slate-400">
            {Object.entries(other).map(([k, v]) => (
              <span key={k} data-testid={`counter-other-${k}`}>
                {k}: <span className="text-[#00FF87]">{v.public}</span> / {v.total} {t("admin.counter_public")}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
